import type { AspectRatio } from '@lumen5/luminary';

import type { DashboardFlowEnum } from 'js/src/client';
import { MaxVideoDurationSeconds } from 'js/src/client';
import { getMaxVideoDurationSeconds } from 'js/src/data/account';
import {
    type AsyncGenerationData,
    type EDITOR_TAB_OPTIONS,
    isDashboardVideoFlow,
    type PendingChart,
    type RecommendedBlueprint,
    type SCENE_GROUP_LOADING_TYPES,
    type VIDEO_INIT_TYPE,
} from 'js/src/data/app/app-types';
import { getBlueprint } from 'js/src/data/blueprints';
import { getTemplateColorPalettes } from 'js/src/data/templates';
import type { NEW_VIDEO_FLOW_CREATION_METHOD } from 'js/src/libs/consts';
import { getQueryParam } from 'js/src/libs/querystring';
import type { RootState } from 'js/src/libs/RootState';

export function isEditorTabsVisible(state: RootState) {
    return state.app.editorTabsVisible;
}

export function getVideoId(state: RootState): number | null {
    return state.app.video_id;
}

export function getVideoSlug(state: RootState): string | null {
    return state.app.video_slug;
}

export function getVideoIdOrThrow(state: RootState): number {
    const videoId = getVideoId(state);
    if (videoId === null) {
        throw new Error('No video id');
    }
    return videoId;
}

export function isToolboxCreatingVideo(state: RootState): boolean {
    return state.app.toolboxCreatingVideo;
}

export function isToolboxTurnActive(state: RootState): boolean {
    return state.app.toolboxTurnActive;
}

export function isToolboxSidebarLatched(state: RootState): boolean {
    return state.app.toolboxSidebarLatched;
}

export function getScriptComposerPlaceholderVideoId(state: RootState): number | null {
    return state.app.scriptComposerPlaceholderVideoId;
}

export function isSceneHandoffActive(state: RootState): boolean {
    return state.app.sceneHandoffActive;
}

export function getCurrentEditorTab(state: RootState): EDITOR_TAB_OPTIONS {
    return state.app.editor_tab;
}

export function getVideoCreationSource(state: RootState) {
    return state.app.videoCreationSource;
}

export function getIsAnimationVideo(state: RootState): boolean {
    return state.app.isAnimationVideo;
}

export function getParentVideoLanguage(state: RootState) {
    return state.app.parentVideoLanguage;
}

export function getVideoDataHash(state: RootState) {
    return state.app.videoDataHash;
}

export function getSaveConflict(state: RootState): boolean {
    return state.app.saveConflict;
}

export function getSharePageUrl(state: RootState) {
    return state.app.sharePageUrl;
}

export function getVideoVisibility(state: RootState) {
    return state.app.videoVisibility;
}

export function getVideoLanguage(state: RootState) {
    return state.app.video_language;
}

export function getVideoLanguageOrThrow(state: RootState) {
    const videoLanguage = getVideoLanguage(state);
    if (!videoLanguage) {
        throw new Error('No video language');
    }
    return videoLanguage;
}

const getLoadingSceneGroup = (state: RootState, sceneGroupId: string) =>
    Object.prototype.hasOwnProperty.call(state.app.loadingSceneGroups, sceneGroupId)
        ? state.app.loadingSceneGroups[sceneGroupId]
        : undefined;

const getLoadingScene = (state: RootState, sceneId: string) =>
    Object.prototype.hasOwnProperty.call(state.app.loadingScenes, sceneId)
        ? state.app.loadingScenes[sceneId]
        : undefined;

export function isSceneGroupLoading(state: RootState, sceneGroupId: string) {
    const loadingData = getLoadingSceneGroup(state, sceneGroupId);
    if (!loadingData) {
        return false;
    }
    return !(loadingData.skipped ?? false);
}

export function getSceneGroupLoadingType(
    state: RootState,
    sceneGroupId: string,
): SCENE_GROUP_LOADING_TYPES | undefined {
    return getLoadingSceneGroup(state, sceneGroupId)?.type;
}

export function getSceneGroupLoadingContext(state: RootState, sceneGroupId: string) {
    return getLoadingSceneGroup(state, sceneGroupId)?.context;
}

export function isSceneGroupLoadingSkipped(state: RootState, sceneGroupId: string) {
    return getLoadingSceneGroup(state, sceneGroupId)?.skipped ?? false;
}

export function getAgentHandoffLoadingSceneGroupIds(state: RootState): string[] {
    // Called from the agent turn-end hook, which runs for every root agent —
    // including in contexts whose store has no app slice — so this must not assume
    // one is present.
    const loadingSceneGroups = state.app?.loadingSceneGroups;
    if (!loadingSceneGroups) {
        return [];
    }
    return Object.keys(loadingSceneGroups).filter(
        (sceneGroupId) => loadingSceneGroups[sceneGroupId].context?.isAgentHandoff === true,
    );
}

export function isSceneLoading(state: RootState, sceneId: string) {
    return getLoadingScene(state, sceneId) !== undefined;
}

export function getSceneLoadingType(
    state: RootState,
    sceneId: string,
): SCENE_GROUP_LOADING_TYPES | undefined {
    return getLoadingScene(state, sceneId)?.type;
}

export function getSceneLoadingContext(state: RootState, sceneId: string) {
    return getLoadingScene(state, sceneId)?.context;
}

export function getSelectedAspectRatio(state: RootState): AspectRatio {
    return state.app.selected_aspect_ratio;
}

export function getSelectedTemplateId(state: RootState): string | null {
    return state.app.selected_template_id;
}

export function getSelectedPaletteIndex(state: RootState): number | null {
    return state.app.selected_palette_index;
}

export function isStoryboardGenerationInProgress(state: RootState) {
    return state.app.isStoryboardGenerating;
}

export function getPendingChart(state: RootState): PendingChart | null {
    return state.app.pendingChart;
}

export function isTextOnMediaGenerating(state: RootState) {
    return state.app.isTextOnMediaGenerating;
}

export function getVideoProjectId(state: RootState) {
    return state.app.video_project;
}

export function getVideoOwnerId(state: RootState) {
    return state.app.video_owner;
}

export function getVideoApprovalStatus(state: RootState) {
    return state.app.videoApprovalStatus;
}

export function getVideoAssignee(state: RootState) {
    return state.app.videoAssignee;
}

export function getVideoName(state: RootState) {
    return state.app.videoName;
}

export function isVideoFeedbackFormVisible(state: RootState) {
    return state.app.isVideoFeedbackFormVisible;
}

export function videoFeedbackFormSource(state: RootState) {
    return state.app.videoFeedbackFormSource;
}

export function getWindowWidth(state: RootState): number | null {
    return state.app.windowWidth;
}

export function getVideoInitType(state: RootState): VIDEO_INIT_TYPE {
    return state.app.videoInitType;
}

export function isAdvancedMediaControlsVisible(state: RootState) {
    return state.app.isAdvancedMediaControlsVisible;
}

export function isChartSettingsVisible(state: RootState) {
    return state.app.isChartSettingsVisible;
}

export function getTransitionSettings(state: RootState) {
    return state.app.transitionSettings;
}

export function chartSettingsSceneId(state: RootState) {
    return state.app.chartSettingsSceneId;
}

export function chartSettingsAssetId(state: RootState) {
    return state.app.chartSettingsAssetId;
}

export function advancedMediaControlsSceneId(state: RootState) {
    return state.app.advancedMediaControlsSceneId;
}

export function advancedMediaControlsAssetId(state: RootState) {
    return state.app.advancedMediaControlsAssetId;
}

export function getSelectedBlueprintId(state: RootState): number | null {
    return state.app.selectedBlueprintId;
}

export function getInitialBlueprintId(state: RootState): number | null {
    return state.app.initialBlueprintId;
}

export function getRecommendedBlueprint(state: RootState): RecommendedBlueprint | null {
    return state.app.recommendedBlueprint;
}

export function getAsyncGenerationTaskId(state: RootState): string | null {
    return state.app.asyncGenerationTaskId;
}

export function getAsyncGenerationData(state: RootState): AsyncGenerationData | null {
    return state.app.asyncGenerationData;
}

export function getIsUsingCustomerBlueprint(state: RootState): boolean {
    const selectedBlueprintId = getInitialBlueprintId(state);
    if (selectedBlueprintId === null) {
        return false;
    }
    return getBlueprint(state, selectedBlueprintId) !== null;
}

export function shouldSkipAgentPipeline(state: RootState): boolean {
    return state.app.skipAgentPipeline;
}

export function getDashboardFlowFromQueryStringOrRedux(state: RootState) {
    const flow = getQueryParam('flow');
    return isDashboardVideoFlow(flow) ? flow : getDashboardFlow(state);
}

export function getDashboardFlow(state: RootState): DashboardFlowEnum | null {
    return state.app.dashboardFlow;
}

export function getCreateVideoFlowAnalyticsMethod(
    state: RootState,
): NEW_VIDEO_FLOW_CREATION_METHOD | null {
    return state.app.createVideoFlowAnalyticsMethod;
}

export function getCreateVideoFlowAnalyticsThemeId(state: RootState): number | string | null {
    return state.app.createVideoFlowAnalyticsThemeId;
}

export function getLoadingCreatorActivityId(state: RootState): string | null {
    return state.app.loadingCreatorActivityId;
}

export function getIsEditingDraft(state: RootState): boolean {
    return state.app.isEditingDraft;
}

export function getProjectId(state: RootState): number | null {
    return state.app.projectId;
}

export function getAutoCreateSceneGroupFromUploadedAssetUuid(state: RootState): string | null {
    return state.app.autoCreateSceneGroupFromUploadedAssetUuid;
}

export function getSelectedBrandKitId(state: RootState): number | null {
    return state.app.selectedBrandKitId;
}

export function isNonDefaultPalette(state: RootState): boolean {
    const selectedPaletteIndex = getSelectedPaletteIndex(state);
    const selectedTemplateId = getSelectedTemplateId(state);

    if (selectedPaletteIndex === null || !selectedTemplateId) {
        return false;
    }

    const palettes = getTemplateColorPalettes(state, selectedTemplateId);
    if (!palettes || palettes.length === 0) {
        return false;
    }

    return selectedPaletteIndex !== 0;
}

export function hasExceededPlanDurationLimit(
    state: RootState,
    videoDurationSeconds: number,
): boolean {
    const maxVideoDurationSeconds = getMaxVideoDurationSeconds(state);
    const isAbsoluteLimitExceeded = videoDurationSeconds >= MaxVideoDurationSeconds.LIMIT;

    return videoDurationSeconds > maxVideoDurationSeconds && !isAbsoluteLimitExceeded;
}
